import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import React, { useEffect, Suspense } from 'react';
import { NavigationProvider } from './context/NavigationContext';
import NavigationProgress from './components/NavigationProgress';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/ToastContainer';
import SystemBanner from './components/SystemBanner';
import ProtectedRoute from './components/ProtectedRoute';
import LockdownGuard from './components/LockdownGuard';
import Navigation from './components/Navigation';
import SessionDebugger from './components/SessionDebugger';
import MobileWelcomeScreen from './components/MobileWelcomeScreen';
import IncomingCallOverlay from './components/IncomingCallOverlay';
import { useSubdomain } from './components/SubdomainRouter';

// ─── Lazy-loaded page components ────────────────────────────
// Each page is loaded on-demand to reduce initial bundle size.
const Home = React.lazy(() => import('./pages/Home'));
const ForBrands = React.lazy(() => import('./pages/ForBrands'));
const ForCreators = React.lazy(() => import('./pages/ForCreators'));
const HowItWorks = React.lazy(() => import('./pages/HowItWorks'));
const About = React.lazy(() => import('./pages/About'));
const Join = React.lazy(() => import('./pages/Join'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies'));
const JoinWaitlist = React.lazy(() => import('./pages/JoinWaitlist'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const RoleSelection = React.lazy(() => import('./pages/RoleSelection'));
const ProfileSetup = React.lazy(() => import('./pages/ProfileSetup'));
const OnboardingFlow = React.lazy(() => import('./pages/OnboardingFlow'));
const PendingApproval = React.lazy(() => import('./pages/PendingApproval'));
const CreatorDashboard = React.lazy(() => import('./pages/CreatorDashboardComplete'));
const FreelancerDashboard = React.lazy(() => import('./pages/FreelancerDashboardMain'));
const JobDetail = React.lazy(() => import('./pages/JobDetailThemed'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const ProfileSettings = React.lazy(() => import('./pages/ProfileSettings'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const PortfolioShowcase = React.lazy(() => import('./pages/PortfolioShowcase'));
const PortfolioManager = React.lazy(() => import('./pages/PortfolioManager'));
const CampaignCreate = React.lazy(() => import('./pages/CampaignCreate'));
const BrowseCampaigns = React.lazy(() => import('./pages/BrowseCampaigns'));
const CampaignDetail = React.lazy(() => import('./pages/CampaignDetail'));
const BrandDashboard = React.lazy(() => import('./pages/BrandDashboard'));

const TemplateLibrary = React.lazy(() => import('./pages/TemplateLibrary'));
const TemplateEditor = React.lazy(() => import('./pages/TemplateEditor'));
const DraftManager = React.lazy(() => import('./pages/DraftManager'));
const BrowseCreators = React.lazy(() => import('./pages/BrowseCreators'));
const Billing = React.lazy(() => import('./pages/Billing'));
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard'));
const AdminVerifications = React.lazy(() => import('./pages/AdminVerifications'));
const AdminAccountApprovals = React.lazy(() => import('./pages/AdminAccountApprovals'));
const BrandAnalyticsDashboard = React.lazy(() => import('./pages/BrandAnalyticsDashboard'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentFailed = React.lazy(() => import('./pages/PaymentFailed'));
const BrandPostJob = React.lazy(() => import('./pages/BrandPostJob'));
const BrandJobManagement = React.lazy(() => import('./pages/BrandJobManagement'));
const BrandJobDeliverables = React.lazy(() => import('./pages/BrandJobDeliverables'));

// NEW REDESIGNED COMPONENTS
const BrandCampaignsNew = React.lazy(() => import('./pages/BrandCampaignsNew'));
const BrandMessagesNew = React.lazy(() => import('./pages/BrandMessagesNew'));
const BrandTemplatesNew = React.lazy(() => import('./pages/BrandTemplatesNew'));
const BrandDraftsNew = React.lazy(() => import('./pages/BrandDraftsNew'));
const TemplateEditorNew = React.lazy(() => import('./pages/TemplateEditorNew'));
const CampaignApplications = React.lazy(() => import('./pages/CampaignApplications'));
const CreatorCampaignDetail = React.lazy(() => import('./pages/CreatorCampaignDetail'));
const CreatorBrowseCampaigns = React.lazy(() => import('./pages/CreatorBrowseCampaigns'));
const CreatorApplications = React.lazy(() => import('./pages/CreatorApplications'));
const BrandFindCreators = React.lazy(() => import('./pages/BrandFindCreators'));
const CreatorInvitations = React.lazy(() => import('./pages/CreatorInvitations'));
const AgencyInvitations = React.lazy(() => import('./pages/AgencyInvitations'));
const CreatorPostJob = React.lazy(() => import('./pages/CreatorPostJob'));
const CreatorJobManagement = React.lazy(() => import('./pages/CreatorJobManagement'));
const CreatorJobDetail = React.lazy(() => import('./pages/CreatorJobDetail'));
const CreatorJobDeliverables = React.lazy(() => import('./pages/CreatorJobDeliverables'));
const CreatorAnalytics = React.lazy(() => import('./pages/CreatorAnalytics'));
const CreatorMessages = React.lazy(() => import('./pages/CreatorMessages'));
const CreatorEarnings = React.lazy(() => import('./pages/CreatorEarnings'));
const BrowseFreelancers = React.lazy(() => import('./pages/BrowseFreelancers'));

// NEW FREELANCER COMPONENTS
const FreelancerDashboardMain = React.lazy(() => import('./pages/FreelancerDashboardMain'));
const FreelancerJobBrowse = React.lazy(() => import('./pages/FreelancerJobBrowse'));
const FreelancerApplications = React.lazy(() => import('./pages/FreelancerApplications'));
const FreelancerProfile = React.lazy(() => import('./pages/FreelancerProfile'));
const FreelancerEarnings = React.lazy(() => import('./pages/FreelancerEarnings'));
const FreelancerMessages = React.lazy(() => import('./pages/FreelancerMessages'));
const FreelancerDeliverables = React.lazy(() => import('./pages/FreelancerDeliverables'));
const FreelancerProjectWorkspace = React.lazy(() => import('./pages/FreelancerProjectWorkspace'));
const ProjectRoom = React.lazy(() => import('./pages/ProjectRoom'));
const ProjectFileManager = React.lazy(() => import('./pages/ProjectFileManager'));
const ProjectRoomsDashboard = React.lazy(() => import('./pages/ProjectRoomsDashboard'));
const FreelancerFileManager = React.lazy(() => import('./pages/FreelancerFileManager'));
const FreelancerPortfolioShowcase = React.lazy(() => import('./pages/FreelancerPortfolioShowcase'));

// AGENCY COMPONENTS
const AgencyDashboard = React.lazy(() => import('./pages/AgencyDashboard'));
const AgencyClientManagement = React.lazy(() => import('./pages/AgencyClientManagement'));
const AgencyClientDetail = React.lazy(() => import('./pages/AgencyClientDetail'));
const AgencyTeamManagement = React.lazy(() => import('./pages/AgencyTeamManagement'));
const AgencyTeamInvite = React.lazy(() => import('./pages/AgencyTeamInvite'));
const AgencyTeamDetail = React.lazy(() => import('./pages/AgencyTeamDetail'));
const AgencyProjects = React.lazy(() => import('./pages/AgencyProjects'));
const AgencyMessages = React.lazy(() => import('./pages/AgencyMessages'));
const AgencyAnalytics = React.lazy(() => import('./pages/AgencyAnalytics'));
const FreelancerAnalytics = React.lazy(() => import('./pages/FreelancerAnalytics'));
const AgencyAutomation = React.lazy(() => import('./pages/AgencyAutomation'));
const AgencySettings = React.lazy(() => import('./pages/AgencySettings'));
const AgencyBilling = React.lazy(() => import('./pages/AgencyBilling'));
const AgencyRegister = React.lazy(() => import('./pages/AgencyRegister'));

const AgencyMeetings = React.lazy(() => import('./pages/AgencyMeetings'));
const AgencyReports = React.lazy(() => import('./pages/AgencyReports'));
const AgencyRevenue = React.lazy(() => import('./pages/AgencyRevenue'));
const AgencyTemplates = React.lazy(() => import('./pages/AgencyTemplates'));
const AgencyResources = React.lazy(() => import('./pages/AgencyResources'));
const AgencySecurity = React.lazy(() => import('./pages/AgencySecurity'));
const AgencyRepresentedCreators = React.lazy(() => import('./pages/AgencyRepresentedCreators'));
const InvoicePreview = React.lazy(() => import('./pages/InvoicePreview'));
const ContractPreview = React.lazy(() => import('./pages/ContractPreview'));
const ContractFlowAssistant = React.lazy(() => import('./pages/ContractFlowAssistant'));
const ContractSessionRoom = React.lazy(() => import('./pages/ContractSessionRoom'));
const MeetingRoom = React.lazy(() => import('./pages/MeetingRoom'));

// ADVANCED FEATURES DASHBOARDS
const FeedbackDashboard = React.lazy(() => import('./pages/FeedbackDashboard'));
const ReferralDashboard = React.lazy(() => import('./pages/ReferralDashboard'));
const RetentionDashboard = React.lazy(() => import('./pages/RetentionDashboard'));
const MarketplaceHealthDashboard = React.lazy(() => import('./pages/MarketplaceHealthDashboard'));
const LinkHelp = React.lazy(() => import('./pages/LinkHelp'));

// TEAM COLLABORATION SHARED PAGES
const TeamAcceptInvitation = React.lazy(() => import('./pages/TeamAcceptInvitation'));

// ADDITIONAL PAGES (previously unrouted)
const AdminSupportInbox = React.lazy(() => import('./pages/AdminSupportInbox'));
const Support = React.lazy(() => import('./pages/Support'));
const AgencyLeads = React.lazy(() => import('./pages/AgencyLeads'));
const AgencyJobDetail = React.lazy(() => import('./pages/AgencyJobDetail'));
const AgencyJobManagement = React.lazy(() => import('./pages/AgencyJobManagement'));
const AgencyPostJob = React.lazy(() => import('./pages/AgencyPostJob'));
const AgencyBrowseCreators = React.lazy(() => import('./pages/AgencyBrowseCreators'));
const AgencyDeals = React.lazy(() => import('./pages/AgencyDeals'));
const CreatorDeals = React.lazy(() => import('./pages/CreatorDeals'));
const PublicContractSign = React.lazy(() => import('./pages/PublicContractSign'));
const HiringApply = React.lazy(() => import('./pages/HiringApply'));
const CareerLanding = React.lazy(() => import('./pages/CareerLanding'));
const CareerJobDetail = React.lazy(() => import('./pages/JobDetail'));
const OpsCampaignsDashboard = React.lazy(() => import('./pages/OpsCampaignsDashboard'));
const ProjectClusterDashboard = React.lazy(() => import('./pages/ProjectClusterDashboard'));
const AIResearch = React.lazy(() => import('./pages/AIResearch'));
const AIAdsWorkflow = React.lazy(() => import('./pages/AIAdsWorkflow'));
const BrandAIUGCStudio = React.lazy(() => import('./pages/BrandAIUGCStudio'));
const BrandQuickStart = React.lazy(() => import('./pages/BrandQuickStart'));
const BrandTeamDetail = React.lazy(() => import('./pages/BrandTeamDetail'));
const BrandTeamInvite = React.lazy(() => import('./pages/BrandTeamInvite'));
const BrandTeamManagement = React.lazy(() => import('./pages/BrandTeamManagement'));
const DisputeDetail = React.lazy(() => import('./pages/DisputeDetail'));
const BrowseAgencies = React.lazy(() => import('./pages/BrowseAgencies'));
const BrandAgencyProfile = React.lazy(() => import('./pages/BrandAgencyProfile'));
const UserGuide = React.lazy(() => import('./pages/UserGuide'));
const Contact = React.lazy(() => import('./pages/Contact'));
const PersonalBrandProfile = React.lazy(() => import('./pages/PersonalBrandProfile'));
const PublicIdentity = React.lazy(() => import('./pages/PublicIdentity'));
const DashDropMobile = React.lazy(() => import('./pages/DashDropMobile'));
const DashDropBridge = React.lazy(() => import('./pages/DashDropBridge'));
const DashDropBridgeMobile = React.lazy(() => import('./pages/DashDropBridgeMobile'));
const DashDropPublic = React.lazy(() => import('./pages/DashDropPublic'));
const DashDropTransfer = React.lazy(() => import('./pages/DashDropTransfer'));
const DashDropMobilePublic = React.lazy(() => import('./pages/DashDropMobilePublic'));
const NetworkHub = React.lazy(() => import('./pages/NetworkHub'));
const RelationshipDashboard = React.lazy(() => import('./pages/RelationshipDashboard'));
const IdentityEditor = React.lazy(() => import('./pages/IdentityEditor'));
const AIScriptHub = React.lazy(() => import('./pages/AIScriptHub'));
const CreatorAIScriptHub = React.lazy(() => import('./pages/CreatorAIScriptHub'));
const FreelancerAIScriptHub = React.lazy(() => import('./pages/FreelancerAIScriptHub'));
const BrandAIScriptHub = React.lazy(() => import('./pages/BrandAIScriptHub'));
const AgencyAIScriptHub = React.lazy(() => import('./pages/AgencyAIScriptHub'));
const AdminAIScriptHub = React.lazy(() => import('./pages/AdminAIScriptHub'));

// TASK BOARD & CRM
const TaskBoard = React.lazy(() => import('./pages/TaskBoard'));
const CRMDashboard = React.lazy(() => import('./pages/CRMDashboard'));

// CALENDAR PAGE
const CalendarPage = React.lazy(() => import('./pages/CalendarPage'));

// ADMIN COMPONENTS
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminUserManagement = React.lazy(() => import('./pages/AdminUserManagement'));
const AdminFinancials = React.lazy(() => import('./pages/AdminFinancials'));
const AdminContentModeration = React.lazy(() => import('./pages/AdminContentModeration'));
const AdminAuditLogs = React.lazy(() => import('./pages/AdminAuditLogs'));
const AdminDisputes = React.lazy(() => import('./pages/AdminDisputes'));
const AdminRevisionEscalations = React.lazy(() => import('./pages/AdminRevisionEscalations'));
const AdminNotifications = React.lazy(() => import('./pages/AdminNotifications'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const AdminExclusiveAccess = React.lazy(() => import('./pages/AdminExclusiveAccess'));
const AdminHiringApplications = React.lazy(() => import('./pages/AdminHiringApplications'));
const AdminJobListings = React.lazy(() => import('./pages/AdminJobListings'));
const AdminReferralManagement = React.lazy(() => import('./pages/AdminReferralManagement'));
const VerificationPortal = React.lazy(() => import('./pages/VerificationPortal'));
const LockdownAccess = React.lazy(() => import('./pages/LockdownAccess'));
const ExclusiveAccess = React.lazy(() => import('./pages/ExclusiveAccess'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Check if we're on DashDrop subdomain
const isDashDropSubdomain = () => {
  const hostname = window.location.hostname;
  return (
    hostname.startsWith('dashdrop.') || 
    hostname === 'dashdrop.linkdash.me' ||
    hostname === 'dashdrop.localhost' ||
    hostname === 'dashdrop.local'
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function ConditionalNavigation() {
  const location = useLocation();
  
  // Hide navigation on dashboard and authenticated pages
  const hideNavRoutes = [
    '/login',
    '/register',
    '/role-selection',
    '/onboarding',
    '/pending-approval',
    '/setup',
    '/lockdown-access',
    '/careers',
    '/hiring/apply',
    '/creator/',
    '/freelancer/',
    '/brand/',
    '/agency/',
    '/admin',
    '/profile-settings',
    '/portfolio-manager',
    '/messages/',
    '/job/',
    '/campaigns',
    '/templates',
    '/drafts',
    '/creators',
    '/projects/',
    '/project-rooms',
    '/contract-flow/',
    '/contract-session/',
    '/meeting/',
    '/users/',
    '/portfolio/',
    '/u/',
    '/dashdrop/',
    '/dashdrop-bridge',
    '/dashdrop-transfer',
    '/dashdrop-mobile/'
  ];
  
  const shouldHideNav = hideNavRoutes.some(route => location.pathname.startsWith(route)) || 
                        location.pathname.match(/^\/profile\/[^/]+$/) || 
                        location.pathname.match(/^\/portfolio\/[^/]+$/);
  
  if (shouldHideNav) {
    return null;
  }
  
  return <Navigation />;
}

function AIScriptsEntry() {
  const { user, loading } = useAuth();
  const [shouldRedirect, setShouldRedirect] = React.useState(false);

  React.useEffect(() => {
    if (user?.role && !loading) {
      // Delay redirect to allow initial render for SEO/crawlers
      const timer = setTimeout(() => setShouldRedirect(true), 150);
      return () => clearTimeout(timer);
    }
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (shouldRedirect) {
    if (user?.role === 'admin') return <Navigate to="/admin/ai-scripts" replace />;
    if (user?.role === 'brand') return <Navigate to="/brand/ai-scripts" replace />;
    if (user?.role === 'creator') return <Navigate to="/creator/ai-scripts" replace />;
    if (user?.role === 'freelancer') return <Navigate to="/freelancer/ai-scripts" replace />;
    if (user?.role === 'agency') return <Navigate to="/agency/ai-scripts" replace />;
  }

  return <AIScriptHub />;
}

/** No-op — transition feedback is handled by the progress bar + dashboard content fade */
function TransitionOverlay() {
  return null;
}

function App() {
  // If on DashDrop subdomain, render DashDrop app
  if (isDashDropSubdomain()) {
    const DashDropApp = React.lazy(() => import('./components/DashDropApp'));
    return (
      <React.Suspense fallback={
        <div className="min-h-screen bg-dark-950 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      }>
        <DashDropApp />
      </React.Suspense>
    );
  }

  const appShell = (
    <AuthProvider>
      <CurrencyProvider>
        <ThemeProvider>
          <ToastProvider>
            <Router>
            <NavigationProvider>
            <ScrollToTop />
            <NavigationProgress />
            <TransitionOverlay />
            <div className="App bg-dark">
              <MobileWelcomeScreen />
              <IncomingCallOverlay />
              <LockdownGuard>
                <ConditionalNavigation />
                <Suspense fallback={
                  <div className="min-h-screen bg-dark flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-dark-400 text-sm">Loading...</span>
                    </div>
                  </div>
                }>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/for-brands" element={<ForBrands />} />
                    <Route path="/for-creators" element={<ForCreators />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/case-studies" element={<CaseStudies />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/join" element={<Join />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/join-waitlist" element={<JoinWaitlist />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/lockdown-access" element={<LockdownAccess />} />
                    
                    {/* Payment Routes */}
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/failed" element={<PaymentFailed />} />

                    <Route path="/role-selection" element={<RoleSelection />} />
                    
                    {/* Unified Onboarding Flow */}
                    <Route 
                      path="/onboarding" 
                      element={
                        <ProtectedRoute>
                          <OnboardingFlow />
                        </ProtectedRoute>
                      } 
                    />
                    {/* Pending Admin Approval */}
                    <Route 
                      path="/pending-approval" 
                      element={
                        <ProtectedRoute>
                          <PendingApproval />
                        </ProtectedRoute>
                      } 
                    />
                    {/* Legacy role-specific onboarding redirects */}
                    <Route path="/creator/onboarding" element={<Navigate to="/onboarding" replace />} />
                    <Route path="/freelancer/onboarding" element={<Navigate to="/onboarding" replace />} />
                    <Route path="/brand/onboarding" element={<Navigate to="/onboarding" replace />} />
                    <Route path="/agency/onboarding" element={<Navigate to="/onboarding" replace />} />
                    
                    <Route 
                      path="/setup/:role" 
                      element={
                        <ProtectedRoute>
                          <ProfileSetup />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Shared contract session assistant */}
                    <Route
                      path="/contract-flow/:kind/:projectId"
                      element={
                        <ProtectedRoute>
                          <ContractFlowAssistant />
                        </ProtectedRoute>
                      }
                    />

                    {/* Unified Project Rooms dashboard (shared across roles) */}
                    <Route
                      path="/project-rooms"
                      element={
                        <ProtectedRoute>
                          <ProjectRoomsDashboard />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/agency/project-rooms"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <ProjectRoomsDashboard />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/brand/project-rooms"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <ProjectRoomsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/contract-session/:kind/:projectId"
                      element={
                        <ProtectedRoute>
                          <ContractSessionRoom />
                        </ProtectedRoute>
                      }
                    />

                    {/* Meeting / Video Call Room */}
                    <Route
                      path="/meeting/:meetingCode"
                      element={
                        <ProtectedRoute>
                          <MeetingRoom />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Brand Routes */}
                    <Route 
                      path="/brand/dashboard" 
                      element={
                        <ProtectedRoute requiredRole="brand" requireCompleteProfile>
                          <BrandDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/brand/ai-scripts"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandAIScriptHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/brand/feedback" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <FeedbackDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/referrals" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <ReferralDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/success" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <RetentionDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/campaigns" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandCampaignsNew />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/campaigns/:id" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CampaignDetail />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/campaigns/:id/edit" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CampaignCreate />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/campaigns/:id/applications" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CampaignApplications />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/campaigns/:id/find-creators" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandFindCreators />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/creators" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrowseCreators />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/brand/freelancers"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrowseFreelancers />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/agency/freelancers"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <BrowseFreelancers />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/brand/creators/profile/:id" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <UserProfile />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/creators/portfolio/:userId" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <PortfolioShowcase />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/brand/agencies"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrowseAgencies />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/agencies/:agencyId"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandAgencyProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/brand/jobs" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandJobManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/files" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <ProjectFileManager role="brand" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/tasks" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <TaskBoard dashboardRole="brand" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/crm" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CRMDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/post-job" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandPostJob />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/job/:jobId/deliverables" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandJobDeliverables />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/templates" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandTemplatesNew />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/templates/create" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <TemplateEditorNew />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/templates/edit/:id" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <TemplateEditorNew />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/drafts" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandDraftsNew />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/profile-settings" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <ProfileSettings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/billing" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <Billing />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/invoices/preview/:kind/:projectId" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <InvoicePreview role="brand" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/contracts/preview/:kind/:projectId" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <ContractPreview role="brand" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/analytics" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandAnalyticsDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/messages" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <Navigate to="/project-rooms" replace />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/brand/guide"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <UserGuide />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/brand/messages/:id" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <MessagesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/help" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <LinkHelp />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/brand/calendar"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CalendarPage dashboardRole="brand" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Creator Routes */}
                    <Route 
                      path="/creator/dashboard" 
                      element={
                        <ProtectedRoute requiredRole="creator" requireCompleteProfile>
                          <CreatorDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/creator/ai-scripts"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorAIScriptHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/creator/feedback" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <FeedbackDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/referrals" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <ReferralDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/success" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <RetentionDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/help" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <LinkHelp />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/campaigns" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorBrowseCampaigns />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/campaigns/:id" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorCampaignDetail />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/contracts/preview/:kind/:projectId" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <ContractPreview role="creator" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/creator/invitations" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorInvitations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/creator/deals"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorDeals />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/creator/post-job" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorPostJob />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/jobs" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorJobManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/creator/jobs/:jobId"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorJobDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/creator/browse-jobs"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <FreelancerJobBrowse />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/creator/files" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <ProjectFileManager role="creator" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/tasks" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <TaskBoard dashboardRole="creator" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/job/:jobId/deliverables" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorJobDeliverables />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/analytics" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorAnalytics />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/applications" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorApplications />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/portfolio-manager" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <PortfolioManager />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/identity" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <IdentityEditor />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/profile-settings" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <ProfileSettings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/messages/:id" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <MessagesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/messages" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <Navigate to="/project-rooms" replace />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/creator/earnings" 
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CreatorEarnings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/creator/agencies"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <BrowseAgencies />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/creator/calendar"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <CalendarPage dashboardRole="creator" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Freelancer Routes */}
                    <Route 
                      path="/freelancer/dashboard" 
                      element={
                        <ProtectedRoute requiredRole="freelancer" requireCompleteProfile>
                          <FreelancerDashboardMain />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/freelancer/ai-scripts"
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerAIScriptHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/freelancer/contracts/preview/:kind/:projectId" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <ContractPreview role="freelancer" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/feedback" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FeedbackDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/referrals" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <ReferralDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/success" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <RetentionDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/jobs" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerJobBrowse />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/applications" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerApplications />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/profile" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerProfile />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/earnings" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerEarnings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/messages" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <Navigate to="/project-rooms" replace />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/deliverables" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerDeliverables />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/project/:projectId" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerProjectWorkspace />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/files" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerFileManager />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/tasks" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <TaskBoard dashboardRole="freelancer" />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/analytics" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerAnalytics />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/portfolio-showcase" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <FreelancerPortfolioShowcase />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/job/:jobId" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <JobDetail />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/portfolio-manager" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <PortfolioManager />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/identity" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <IdentityEditor />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/profile-settings" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <ProfileSettings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/messages/:id" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <MessagesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/freelancer/help" 
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <LinkHelp />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/freelancer/guide"
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <UserGuide />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/freelancer/calendar"
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <CalendarPage dashboardRole="freelancer" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Agency Routes */}
                    <Route
                      path="/agency/dashboard"
                      element={
                        <ProtectedRoute requiredRole="agency" requireCompleteProfile>
                          <AgencyDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/ai-scripts"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyAIScriptHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/clients"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyClientManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/clients/:clientId"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyClientDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/team"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyTeamManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/team/invite"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyTeamInvite />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/team/:teamId"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyTeamDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/projects"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyProjects />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/invitations"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyInvitations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/deals"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyDeals />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/analytics"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/automation"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyAutomation />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/settings"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencySettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/billing"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyBilling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/files"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <ProjectFileManager role="agency" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/creators"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyBrowseCreators />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/represented-creators"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyRepresentedCreators />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/guide"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <UserGuide />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/calendar"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <CalendarPage dashboardRole="agency" />
                        </ProtectedRoute>
                      }
                    />

                    {/* Unified Project Room - shared across roles */}
                    <Route
                      path="/projects/:kind/:id"
                      element={
                        <ProtectedRoute>
                          <ProjectRoom />
                        </ProtectedRoute>
                      }
                    />

                    {/* Shared Routes - accessible by profile ID */}
                    <Route 
                      path="/profile/:id" 
                      element={
                        <ProtectedRoute>
                          <UserProfile />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/portfolio/:userId" 
                      element={
                        <ProtectedRoute>
                          <PortfolioShowcase />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Team collaboration shared routes */}
                    <Route
                      path="/teams/accept-invitation/:token"
                      element={
                        <ProtectedRoute>
                          <TeamAcceptInvitation />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/onboarding"
                      element={<Navigate to="/role-selection" replace />}
                    />

                    {/* Legacy routes - kept for backwards compatibility */}
                    <Route 
                      path="/job/:jobId" 
                      element={
                        <ProtectedRoute>
                          <JobDetail />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/profile-settings" 
                      element={
                        <ProtectedRoute>
                          <ProfileSettings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/messages/:id" 
                      element={
                        <ProtectedRoute>
                          <MessagesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/portfolio-manager" 
                      element={
                        <ProtectedRoute>
                          <PortfolioManager />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/identity" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <IdentityEditor />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/brand/createcampaigns" 
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <CampaignCreate />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Admin Routes */}
                    <Route 
                      path="/admin" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/admin/ai-scripts"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminAIScriptHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/admin/users" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminUserManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/admin/verifications"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminVerifications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/approvals"
                      element={
                        <ProtectedRoute requiredRole="support_staff">
                          <AdminAccountApprovals />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/admin/financials" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminFinancials />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/content" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminContentModeration />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/audit" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminAuditLogs />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/disputes" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminDisputes />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/revision-escalations" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminRevisionEscalations />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/notifications" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminNotifications />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/verification"
                      element={
                        <ProtectedRoute>
                          <VerificationPortal />
                        </ProtectedRoute>
                      }
                    />
                    <Route 
                      path="/admin/settings" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminSettings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/hiring" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminHiringApplications />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/job-listings" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminJobListings />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/referrals" 
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminReferralManagement />
                        </ProtectedRoute>
                      } 
                    />
                    <Route
                      path="/admin/hiring-applications"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminHiringApplications />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/exclusive-access"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminExclusiveAccess />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/exclusive-access" element={<ExclusiveAccess />} />

                    {/* Public pages (no auth required) */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/user-guide" element={<UserGuide />} />
                    <Route
                      path="/ai-scripts"
                      element={<AIScriptsEntry />}
                    />
                    <Route path="/hiring/apply" element={<HiringApply />} />
                    <Route path="/careers" element={<CareerLanding />} />
                    <Route path="/careers/:slug" element={<CareerJobDetail />} />
                    <Route path="/browse/freelancers" element={<BrowseFreelancers />} />
                    <Route path="/browse/agencies" element={<BrowseAgencies />} />
                    <Route path="/contracts/sign/:token" element={<PublicContractSign />} />
                    <Route path="/personal-brand/:handle" element={<PersonalBrandProfile />} />
                    <Route path="/u/:slug" element={<PublicIdentity />} />
                    <Route path="/dashdrop/:token" element={<DashDropMobile />} />
                    <Route path="/dashdrop-bridge" element={<DashDropBridge />} />
                    <Route path="/dashdrop-bridge/:roomId" element={<DashDropBridgeMobile />} />
                    
                    {/* DashDrop Public - Free File Transfer */}
                    <Route path="/dashdrop" element={<DashDropPublic />} />
                    <Route path="/dashdrop-transfer" element={<DashDropTransfer />} />
                    <Route path="/dashdrop-mobile/:roomId" element={<DashDropMobilePublic />} />

                    {/* Network Hub */}
                    <Route
                      path="/network"
                      element={
                        <ProtectedRoute>
                          <NetworkHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/network/:userId"
                      element={
                        <ProtectedRoute>
                          <RelationshipDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Support (authenticated) */}
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <Support />
                        </ProtectedRoute>
                      }
                    />

                    {/* Brand Additional Routes */}
                    <Route
                      path="/brand/quick-start"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandQuickStart />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/brand/start"
                      element={<Navigate to="/brand/quick-start" replace />}
                    />
                    <Route
                      path="/brand/ai-ugc"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandAIUGCStudio />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/ai-research"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <AIResearch />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/ai-ads"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <AIAdsWorkflow />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/team"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandTeamManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/team/invite"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandTeamInvite />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brand/team/:teamId"
                      element={
                        <ProtectedRoute requiredRole="brand">
                          <BrandTeamDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Creator Additional Routes */}
                    <Route
                      path="/creator/ai-research"
                      element={
                        <ProtectedRoute requiredRole="creator">
                          <AIResearch />
                        </ProtectedRoute>
                      }
                    />

                    {/* Freelancer AI Routes */}
                    <Route
                      path="/freelancer/ai-research"
                      element={
                        <ProtectedRoute requiredRole="freelancer">
                          <AIResearch />
                        </ProtectedRoute>
                      }
                    />

                    {/* Agency Additional Routes */}
                    <Route
                      path="/agency/ai-research"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AIResearch />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/campaigns"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <CreatorBrowseCampaigns />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/campaigns/:id"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <CampaignDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/leads"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyLeads />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/tasks"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <TaskBoard dashboardRole="agency" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/crm"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <CRMDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/jobs"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyJobManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/portfolio-manager"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <PortfolioManager />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/identity"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <IdentityEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/post-job"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyPostJob />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/job/:jobId"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyJobDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/messages"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <Navigate to="/project-rooms" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/messages/:id"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <MessagesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/meetings"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyMeetings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/reports"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyReports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/revenue"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyRevenue />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/templates"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyTemplates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/resources"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencyResources />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/security"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <AgencySecurity />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/help"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <LinkHelp />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/profile-settings"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <ProfileSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/invoices/preview/:kind/:projectId"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <InvoicePreview role="agency" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/contracts/preview/:kind/:projectId"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <ContractPreview role="agency" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/feedback"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <FeedbackDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agency/referrals"
                      element={
                        <ProtectedRoute requiredRole="agency">
                          <ReferralDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Additional Routes */}
                    <Route
                      path="/admin/support"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminSupportInbox />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/ops-campaigns"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <OpsCampaignsDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Dispute detail (authenticated) */}
                    <Route
                      path="/disputes/:id"
                      element={
                        <ProtectedRoute>
                          <DisputeDetail />
                        </ProtectedRoute>
                      }
                    />

                    {/* Project clusters */}
                    <Route
                      path="/project-clusters/:kind/:projectId"
                      element={
                        <ProtectedRoute>
                          <ProjectClusterDashboard />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch-all redirect */}
                    <Route
                      path="*"
                      element={
                        <div className="min-h-screen bg-dark-950 text-white flex items-center justify-center px-6">
                          <div className="max-w-md w-full text-center">
                            <div className="text-3xl font-semibold">404</div>
                            <div className="mt-2 text-dark-400">Page not found</div>
                            <div className="mt-6">
                              <a
                                href="/"
                                className="inline-flex items-center justify-center px-5 py-3 bg-surface-light hover:bg-surface-light border border-surface-border rounded-xl font-bold transition-all"
                              >
                                Go Home
                              </a>
                            </div>
                          </div>
                        </div>
                      }
                    />
                </Routes>
                </Suspense>
              </LockdownGuard>
              
              {/* Session Debugger - only shows in development */}
              <SessionDebugger />
            </div>
          </NavigationProvider>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </CurrencyProvider>
  </AuthProvider>
  );

  if (!GOOGLE_CLIENT_ID) {
    return appShell;
  }

  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{appShell}</GoogleOAuthProvider>;
}

export default App;
